import { Component, ViewChild, ChangeDetectionStrategy, forwardRef, Input, ElementRef } from '@angular/core'
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { SessionStorageService } from 'ngx-store'

@Component({
  selector: 'filterbox',
  templateUrl: './filterbox.component.pug',
  styleUrls: ['./filterbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterboxComponent),
      multi: true,
    },
    SessionStorageService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterboxComponent implements ControlValueAccessor {
  @Input() text = this.translate.instant('Filter')
  @Input() button = false
  @Input() autofocus = true
  @Input() sessionPersist: string|undefined
  @ViewChild('input', { read: NG_VALUE_ACCESSOR, 'static': true }) private cva: ControlValueAccessor
  @ViewChild('input') private input: ElementRef<HTMLInputElement>
  _model: string

  private viewInitResolver: CallableFunction
  private viewInitPromise = new Promise(resolve => {
    this.viewInitResolver = resolve
  })

  constructor (
    private sessionStorage: SessionStorageService,
    private translate: TranslateService,
  ) { }

  ngAfterViewInit () {
    if (this.autofocus) {
      this.input.nativeElement.focus()
    }
    this.viewInitResolver()
    if (this.sessionPersist) {
      const saved = this.sessionStorage.get(this.sessionPersist)
      if (saved) {
        this._model = saved
      }
    }
  }

  // ControlValueAccessor
  async writeValue (value: boolean) {
    await this.viewInitPromise
    this.cva.writeValue(value)
  }

  async registerOnChange (fn) {
    await this.viewInitPromise
    fn(this._model)
    this.cva.registerOnChange(value => {
      fn(value)
      if (this.sessionPersist) {
        this.sessionStorage.set(this.sessionPersist, value)
      }
    })
  }

  async registerOnTouched (fn) {
    await this.viewInitPromise
    this.cva.registerOnTouched(fn)
  }

  async setDisabledState (isDisabled) {
    await this.viewInitPromise
    this.cva.setDisabledState?.(isDisabled)
  }
}
